<template>
  <div ref="container" class="relative">
    <slot name="label">
      <div v-if="showLabel && label" class="mx-auto">
        <FieldLabel class="pl-1" v-bind="{ label }" />
      </div>
    </slot>

    <slot name="anchor" />

    <transition name="dropdown">
      <aside
        ref="popup"
        class="absolute my-2 rounded-md border bg-white shadow-lg z-10 overflow-hidden"
        :class="[
          open ? '' : 'hidden',
          menuPaddingClass,
          menuBorderClass,
          alignmentStyle,
          placementStyle,
          useContainerWidth ? 'w-full' : '',
        ]"
        :style="
          useContainerWidth
            ? {}
            : {
                width: `${calculatedMenuWidth}px`,
                transform: useMenuOffset ? `translateX(${offset}px)` : '',
              }
        "
      >
        <div
          :class="[menuHeight ? 'overflow-y-auto' : '']"
          :style="{
            'max-height': menuHeight ? menuHeight : '',
          }"
        >
          <slot name="content" />
        </div>
      </aside>
    </transition>
  </div>
</template>

<script>
import FieldLabel from '@/components/form/FieldLabel'

export default {
  components: { FieldLabel },

  props: {
    /*
     * Dropdown menu padding
     */
    menuPaddingClass: { type: String, default: 'p-2' },

    /*
     * Dropdown menu padding
     */
    menuBorderClass: { type: String, default: 'border-gray-100' },

    /*
     * Set width of the menu (in rems)
     */
    menuWidth: { type: String, default: '15rem' },

    /*
     * Set height of the menu (in rems)
     */
    menuHeight: { type: String, default: '' },

    /*
     * The visible state of the popup.
     */
    open: { type: Boolean, default: false },

    /*
     * Vertical placement of the menu relative to the button
     */
    placement: { type: String, default: 'bottom' },

    /*
     * Horizontal alignment of the menu relative to the sides of the button
     */
    alignment: { type: String, default: 'left' },

    /*
     * Padding to leave between the edge of the parent and the menu when offset
     */
    menuPadding: { type: String, default: '1rem' },

    /*
     * Query selector for the offset container
     */
    parentSelector: { type: String, default: 'body' },

    /*
     * Match the width of the container/anchor at - ignores offset
     */
    useContainerWidth: { type: Boolean, default: false },

    /*
     * Stretch menu width if container is wider than menu
     */
    stretchToContainerWidth: { type: Boolean, default: true },

    /*
     * Label to display above the menu
     */
    label: { type: String, default: null },

    /*
     * Show the label above the menu
     */
    showLabel: { type: Boolean, default: false },
  },

  data: () => ({
    useMenuOffset: false,
    offset: 0,
    calculatedMenuWidth: 0,
  }),

  computed: {
    /*
     * Corresponding tailwind styles for each alignment type
     */
    alignmentStyle() {
      switch (this.alignment) {
        case 'left':
          return 'left-0'

        case 'right':
          return 'right-0'

        default:
          return ''
      }
    },

    /*
     * Corresponding tailwind styles for each placement type
     */
    placementStyle() {
      switch (this.placement) {
        case 'bottom':
          return 'top-full'

        case 'top':
          return 'bottom-full'

        default:
          return ''
      }
    },
  },

  watch: {
    async open(isOpen) {
      if (isOpen && !this.useContainerWidth) {
        this.offsetMenu(this.menuPadding)
      }
    },
  },

  mounted() {
    window.addEventListener('click', this.handleFocusChange, true)
    window.addEventListener('resize', this.recalculateOffset)
  },

  beforeDestroy() {
    window.removeEventListener('click', this.handleFocusChange, true)
    window.removeEventListener('resize', this.recalculateOffset)
  },

  methods: {
    handleFocusChange(e) {
      // Get the currently focused element
      const currentTarget = e.target
      if (
        this.open &&
        this.$refs.container &&
        !this.$refs.container.contains(currentTarget)
      ) {
        this.$emit('change', false)
      }
    },

    convertRemToPixels(rem) {
      return (
        parseFloat(rem) *
        parseFloat(getComputedStyle(document.documentElement).fontSize)
      )
    },

    /*
     *  Offset menu
     */
    offsetMenu(padding = '1rem', breakpoint = 0) {
      /**
       * Get parent dimensions
       */
      const parent = document
        .querySelector(this.parentSelector)
        .getBoundingClientRect()
      const parentLeft =
        Math.round(parent.left) + this.convertRemToPixels(padding)
      const parentRight =
        Math.round(parent.right) - this.convertRemToPixels(padding)
      const parentWidth = Math.round(parent.width)

      /**
       * Get container dimensions
       */
      const container = this.$refs.container.getBoundingClientRect()
      const containerLeft = Math.round(container.left)
      const containerRight = Math.round(container.right)
      const containerWidth = Math.round(container.width)

      /**
       * Get menu width
       *  - Compare max allowed width by parent and the desired menu width
       */
      const maxAllowedMenuWidth = parentRight - parentLeft
      const desiredMenuWidth = this.convertRemToPixels(this.menuWidth)
      let menuWidth = Math.min(maxAllowedMenuWidth, desiredMenuWidth)

      /**
       * Stretch the menu width to match the container width (optionally)
       */
      if (this.stretchToContainerWidth) {
        menuWidth = Math.max(containerWidth, menuWidth)
      }

      /**
       * Offset variables
       */
      let useMenuOffset = false
      let offset = 0

      /**
       * Calculate offset (if needed)
       * - If aligned to left side of container, use the right edge of the menu
       * - If aligned to right side of container, use the left edge of the menu
       */
      if (this.$refs.container && parentWidth >= breakpoint) {
        const menuRight = containerLeft + menuWidth
        const menuLeft = containerRight - menuWidth

        if (this.alignment === 'left' && menuRight > parentRight) {
          useMenuOffset = true
          offset = parentRight - menuRight
        } else if (this.alignment === 'right' && menuLeft < parentLeft) {
          useMenuOffset = true
          offset = parentLeft - menuLeft
        }
      }

      /**
       * Set final values
       */
      this.calculatedMenuWidth = menuWidth
      this.useMenuOffset = useMenuOffset
      this.offset = offset
      return offset
    },

    recalculateOffset() {
      if (this.open && !this.useContainerWidth) {
        this.offsetMenu()
      }
    },
  },
}
</script>
